import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { itemDisplayName } from "@/lib/inventory";
import { useAdminOrgStore } from "@/stores/admin-org";
import { COURSE_LABELS, courseOf } from "./course";
import { costWarning, type ProcessingStatus } from "./menu-model";
import { BackLink, CostCell, LoadError, RowsSkeleton } from "./menu-parts";
import { useSiteCurrency } from "./use-menus";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Rows shown before "Show all", as on the menu page. */
const PAGE = 8;

interface DefaultMenuIngredient {
  inventory_item_id: string;
  description: string;
  simple_name: string | null;
  clean_name: string | null;
  top_category_slug: string | null;
  subcategory_slug: string | null;
}

interface DefaultMenuRecipe {
  id: string;
  name: string;
  meal_periods: string[];
  description: string | null;
  processing_status: ProcessingStatus;
  ingredients: DefaultMenuIngredient[];
  category_slug: string | null;
  unmatched_ingredient_count: number;
  cost_per_portion: number | null;
  portion_weight_kg: number | null;
}

interface DefaultMenu {
  name: string;
  status: string;
  error_message: string | null;
  recipe_count: number;
  catalogue_name: string | null;
  recipes: DefaultMenuRecipe[];
}

const PERIOD_ORDER = ["Breakfast", "Lunch", "Dinner", "All Day"];
const PERIOD_RANK: Record<string, number> = Object.fromEntries(PERIOD_ORDER.map((p, i) => [p, i]));

/**
 * The platform default (fallback) menu, read-only: the menu page's look without its controls
 * (POS2-1456). A row opens its ingredients in place. Platform admins manage the menu from
 * /admin/default-menu.
 */
export default function DefaultMenuPage() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const currency = useSiteCurrency(useAdminOrgStore((s) => s.siteId));
  const menuQuery = useQuery<DefaultMenu | null>({
    queryKey: ["default-menu"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/default-menu`, { headers });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to load the default menu");
      return (await res.json()).data;
    },
  });

  return (
    <div className="mx-auto max-w-5xl">
      <BackLink label={t("menus.title")} to="/menus" />
      {menuQuery.isPending ? (
        <RowsSkeleton />
      ) : menuQuery.isError ? (
        <LoadError onRetry={() => menuQuery.refetch()} />
      ) : menuQuery.data ? (
        <DefaultMenuView menu={menuQuery.data} currency={currency} />
      ) : (
        <div className="rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center text-sm text-gray-500">
          {t("menus.noDefaultMenu")}
        </div>
      )}
    </div>
  );
}

function DefaultMenuView({ menu, currency }: { menu: DefaultMenu; currency: string }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [opened, setOpened] = useState<Set<string>>(new Set());

  const periodRank = (r: DefaultMenuRecipe) =>
    Math.min(99, ...r.meal_periods.map((p) => PERIOD_RANK[p] ?? 99));
  const needle = query.trim().toLowerCase();
  const filtered = [...menu.recipes]
    .sort((a, b) => periodRank(a) - periodRank(b) || a.name.localeCompare(b.name))
    .filter((r) => r.name.toLowerCase().includes(needle));
  const visible = showAll || needle ? filtered : filtered.slice(0, PAGE);
  const th = "text-xs uppercase tracking-wide text-gray-400";

  function toggle(id: string) {
    setOpened((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <div className="mb-5 min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="truncate text-lg font-medium text-gray-900">{menu.name}</h1>
          <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
            {t("menus.fallbackBadge")}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-gray-500">
          {[t("menus.groups.default"), menu.catalogue_name].filter(Boolean).join(" · ")}
        </p>
        <p className="text-xs text-gray-400">
          {t("menus.defaultMenuSubtitle", { count: menu.recipe_count })}
        </p>
      </div>

      {menu.status === "failed" && (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {menu.error_message ?? t("menus.detail.readFailed")}
        </p>
      )}

      {menu.recipes.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-16 text-center">
          <p className="text-sm font-medium text-gray-900">{t("menus.noRecipesYet")}</p>
          <p className="mt-1 text-sm text-gray-500">{t("menus.noRecipesProcessedHint")}</p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex h-9 items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("menus.detail.search")}
                aria-label={t("menus.detail.search")}
                className="h-9 pl-9"
              />
            </div>
            <span className="shrink-0 text-sm text-gray-500">
              {t("menus.detail.recipeCount", { count: menu.recipes.length })}
            </span>
          </div>

          <div className="overflow-hidden rounded-lg border border-black/[0.06] bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={`pl-4 ${th}`}>{t("menus.columnRecipe")}</TableHead>
                  <TableHead className={`hidden w-32 sm:table-cell ${th}`}>
                    {t("menus.detail.columnCourse")}
                  </TableHead>
                  <TableHead className={`w-20 pr-4 text-right sm:w-28 ${th}`}>
                    {t("menus.columnCostPerPortion")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-10 text-center text-sm text-gray-500">
                      {t("menus.detail.noResults", { query: query.trim() })}
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((recipe) => {
                  const course = courseOf(recipe.category_slug);
                  const courseLabel = course ? t(COURSE_LABELS[course]) : null;
                  const warning = costWarning(t, recipe);
                  const isOpen = opened.has(recipe.id);
                  return (
                    <Fragment key={recipe.id}>
                      <TableRow
                        aria-expanded={isOpen}
                        onClick={() => toggle(recipe.id)}
                        className="cursor-pointer hover:bg-black/[0.02]"
                      >
                        <TableCell className="whitespace-normal py-2.5 pl-4">
                          <div className="flex items-start gap-2">
                            {isOpen ? (
                              <ChevronDown className="mt-0.5 size-4 shrink-0 text-gray-400" />
                            ) : (
                              <ChevronRight className="mt-0.5 size-4 shrink-0 text-gray-400" />
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900">{recipe.name}</p>
                              {warning ? (
                                <p className="text-xs text-amber-700">{warning}</p>
                              ) : (
                                <p className="text-xs text-gray-400">{t("menus.detail.matched")}</p>
                              )}
                              {courseLabel && (
                                <p className="text-xs text-gray-400 sm:hidden">{courseLabel}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden text-sm text-gray-500 sm:table-cell">
                          {courseLabel ?? "-"}
                        </TableCell>
                        <TableCell className="pr-4 text-right">
                          <CostCell
                            perPortion={recipe.cost_per_portion}
                            weightKg={recipe.portion_weight_kg}
                            currency={currency}
                          />
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-gray-50/40 hover:bg-gray-50/40">
                          <TableCell colSpan={3} className="whitespace-normal py-2 pl-10 pr-4">
                            {recipe.description && (
                              <p className="pb-1 text-xs text-gray-500">{recipe.description}</p>
                            )}
                            {recipe.ingredients.length === 0 ? (
                              <p className="py-1 text-sm text-gray-400">
                                {recipe.processing_status === "ready"
                                  ? t("menus.noIngredientsLinked")
                                  : t("menus.ingredientsPending")}
                              </p>
                            ) : (
                              <ul className="space-y-1 py-1 text-sm">
                                {recipe.ingredients.map((ing) => (
                                  <li
                                    key={ing.inventory_item_id}
                                    className="flex flex-wrap items-center gap-2"
                                  >
                                    <span className="text-gray-700">{itemDisplayName(ing)}</span>
                                    {ing.top_category_slug && (
                                      <Badge variant="secondary" className="text-[10px]">
                                        {ing.top_category_slug}
                                      </Badge>
                                    )}
                                    {ing.subcategory_slug && (
                                      <Badge variant="outline" className="text-[10px]">
                                        {ing.subcategory_slug}
                                      </Badge>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            {!needle && filtered.length > PAGE && (
              <div className="flex items-center justify-between border-t border-black/[0.06] px-4 py-2.5 text-sm">
                <span className="text-gray-500">
                  {t("menus.detail.showing", { shown: visible.length, total: filtered.length })}
                </span>
                <button
                  type="button"
                  className="font-medium text-gray-700 hover:text-gray-900"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? t("menus.detail.showFewer") : t("menus.detail.showAll")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
